import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import WSDateField from "../../../Components/DateField/WSDateField";
import { WSIcons } from "../../../commonFunctions/iIcons";
import {
  HeaderList,
  IHeaderListActions
} from "../../../Components/Header/HeaderList";
import WSSelectField from "../../../Components/Select/WSSelect";
import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSTimePicker from "../../../Components/WSTimePicker/WSTimePicker";
import WSTextAreaField from "../../../Components/WSTextArea/WSTextArea";
import {
  callAPI,
  clearRequest,
  getLocalValue,
  LoacalVariables,
  Method
} from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import dayjs from "dayjs";

// Define Product Type;
interface Product {
  id: number;
  name: string;
  price: string;
  service: string;
}

// Sample product data;
const products: Product[] = [
  { id: 1, name: "Laundry By Weight", price: "₹69/kg", service: "Wash & Fold" },
  { id: 2, name: "Shirt", price: "₹80/pc", service: "Dry Clean" },
  { id: 3, name: "T-Shirt", price: "₹75/pc", service: "Dry Clean" }
];

interface ShipAddress {
  name: string;
  address: string;
  phone: string;
}

// Define the type for a single bill
export interface Bill {
  id: number;
  customer: string;
  totalAmount: number;
  paidAmount: number;
  dueAmount: number;
  orderItems: any[];
  date: string;
  shippingAddress?: ShipAddress;
}

interface OrderProps {
  id: number;
  customer: string;
  mobileNumber: string;
  orderItems: any[];

  status: "pending"; // Default status
  pickupDate: string;
  expectedDeliveryDate: string;
  shippingAddress: {
    name: string;
    phone: string;
    address: string;
    city: string;
    pincode: string;
    landmark?: string;
  };
  totalPrice: string;
}

export const PickupOrder: React.FC = () => {
  const { control, handleSubmit, setValue } = useForm();
  const navigate = useNavigate();

  const params = useParams();
  const id = params?.id;
  const { showMessage } = useSnackbar();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function getOrderById(id: any) {
      try {
        const response: any = await callAPI("pickup", Method.GET, null, id);
        

        if (response) {
          // setValue("requestedDate", response.requestedDate);
          setValue("scheduledPickupTime", response.scheduledPickupTime);
          setValue("pickupAddress", response.address);
          setValue("notes", response.notes);
          setValue("customerId", response.customerId);
          setValue("franchiseId", response.franchiseId);

          // Convert requestedDate to MM/DD/YYYY format;
          const formattedDate = dayjs(response.requestedDate).format(
            "MM/DD/YYYY"
          );
          setValue("requestedDate", formattedDate);
        }
      } catch (error) {
        console.error("Failed to retrieve data", error);
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      getOrderById(id);
    }
  }, [id, setValue]);

  const handleCreateOrder = async (data: any) => {
    

    try {
      let request = {
        franchiseId: getLocalValue(LoacalVariables.FranchiseId),
        customerId: data.customerId,
        requestedDate: dayjs(data.requestedDate).format(
          "YYYY-MM-DDTHH:mm:ss.SSS"
        ),
        scheduledPickupTime: data.scheduledPickupTime,
        notes: data.notes,
        address: data.pickupAddress,
        status: "New"
      };

      
      const newRequest = clearRequest(request);

      const endpoint = id ? `pickup/${id}` : "pickup";

      const method = id ? Method.PUT : Method.POST;

      const response: any = await callAPI(endpoint, method, newRequest);
      

      if (response && id) {
        showMessage(response, "success");
        navigate("/pickup-order/list");
      }else {
        showMessage(response?.message, "success");
        navigate("/pickup-order/list");
      }
    } catch (e: any) {
      console.error("error is", e);
      showMessage(e.error);
    }
  };

  const handleCancelClick = () => {
    navigate("/");
  };

  const customerSource = {
    url: "customers/search",
    filter: [{
          "field": "FranchiseId",
          "operator": "eq",
          "value": getLocalValue(LoacalVariables.FranchiseId)
        }],
    sort: [],
    page: 1,
    pageSize: 100000
  };

  const handleTransform = (data: any) => {
    let allData: Array<any> = [];

    data.map((value: any) => {
      allData.push({ label: value.fullName, value: value.customerId });
    });

    return allData;
  };

  const actions: IHeaderListActions[] = [
    {
      type: "icon",
      text: "Cancel",
      size: "small",
      onClick: handleCancelClick,
      variant: "outlined",
      color: "error",
      icon: <WSIcons.CancelIcon />
    },
    {
      type: "icon",
      text: "Save",
      size: "small",
      buttonType: "submit",
      variant: "outlined",
      color: "success",
      icon: <WSIcons.SaveIcon />
    }
  ];

  return (
    <>
      <form onSubmit={handleSubmit(handleCreateOrder)}>
        <HeaderList
          type="card"
          title="Pickup An Order"
          id="pickup-order"
          headerSx={{
            marginTop: 0,
            backgroundColor: "#7D79C2",
            color: "white",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: 2
          }}
          actions={actions}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "10px", padding: 3, mt: 1 }}>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  Pickup Details
                </Typography>

                <Grid item xs={12} sm={6} mb={2}>
                  <WSSelectField
                    id="customerId"
                    label={"Select Customer"}
                    name="customerId"
                    control={control}
                    source={customerSource}
                    rules={{ required: "please select a customer" }}
                    transFormData={handleTransform}
                  />
                </Grid>

                <WSDateField
                  label="Requested Date"
                  id="requestedDate"
                  name="requestedDate"
                  control={control}
                  rules={{ required: "RequestedDate is required" }}
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <WSTimePicker
                    label="Scheduled Pickup Time"
                    id="scheduledPickupTime"
                    name="scheduledPickupTime"
                    control={control}
                    rules={{ required: "Scheduled PickupTime is required" }}
                  />
                </Box>

                <WSTextField
                  label="Pickup Address"
                  id="pickupAddress"
                  name="pickupAddress"
                  control={control}
                  rules={{ required: "Pickup address is required" }}
                  sx={{ mt: 2, mb: 2 }}
                />

                <WSTextAreaField
                  id="notes"
                  label="Notes"
                  name="notes"
                  control={control}
                  rows={3}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </>
  );
};
